import * as React from 'react';
import { CreateEmailObjectRequest, DynamicConfiguration, EmailObjectSummary } from './types';
import { useLocalStorage } from '../useLocalStorage';
import { CMClient } from './CMClient/CMClient';
import { ApiVersion, AuthError, TokenExpiredError } from './types';
import { useNavigation } from '../routing';
import { getApiUrl, formatUrl } from './clientFunctions';
import { RawEmailData, StakeholderCreationRequest, StakeholderInformation } from './types/CreateEmailObjectRequest';
import { getChildren, PropsWithOptionalFunctionalChildren } from '../../helpers/functionalChildren';
import { InvalidSystemError } from './types/ClientTypes';

type MatchingRecords = (emails: Office.EmailAddressDetails[]) => {
  isLoading: boolean;
  matchingRecords: StakeholderInformation[];
  refetch: () => void;
};

export interface ClientContextValue {
  configuration: DynamicConfiguration;
  initiateAuth: (enterpriseUrl: string) => Promise<boolean>;
  pushEmail: (emailData: CreateEmailObjectRequest) => Promise<EmailObjectSummary>;
  useFetchStakeholders: MatchingRecords;
  useFetchUsers: MatchingRecords;
  enterpriseUrl?: string;
  isAuthenticated: boolean;
  logout: () => void;
  createStakeholder: (data: StakeholderCreationRequest) => Promise<StakeholderInformation>;
  checkSummary: (email: RawEmailData) => Promise<EmailObjectSummary>;
  refetchConfiguration: () => Promise<DynamicConfiguration>;
}

export const ClientContext = React.createContext<ClientContextValue | null>(null);

interface ClientProviderProps {
  apiVersion?: ApiVersion;
}

const ClientProvider = ({ children, apiVersion }: PropsWithOptionalFunctionalChildren<ClientProviderProps, ClientContextValue>) => {
  const [enterpriseUrl, setEnterpriseUrl] = useLocalStorage<string>('enterpriseUrl');
  const [token, setToken, clearToken] = useLocalStorage<string>('token');
  const [configuration, setConfiguration] = React.useState<DynamicConfiguration>(null);
  const { navigate } = useNavigation();

  const clientRef = React.useRef<CMClient>();

  React.useEffect(() => {
    if (!enterpriseUrl) {
      clearToken();
      return;
    }

    clientRef.current = new CMClient(getApiUrl(enterpriseUrl), apiVersion);
  }, [enterpriseUrl, apiVersion, clearToken]);

  const logout = React.useCallback(() => {
    clearToken();
    clientRef.current?.clearAuth();
    setConfiguration(null);
    navigate('login');
  }, [navigate, clearToken]);

  const handleAuthError = React.useCallback(
    (error: Error) => {
      console.error('Authentication error:', error);
      if (error instanceof TokenExpiredError || error instanceof AuthError) {
        logout();
      }
    },
    [logout],
  );

  const initiateAuth = React.useCallback(
    async (url: string) => {
      const formattedUrl = formatUrl(url);
      setEnterpriseUrl(formattedUrl);

      // Prevent user from logging in at all if the request fails.
      // This will catch invalid URL, feature not enabled, CORS and
      // various other issues.

      const isEnabled = await clientRef.current.isEnabled();
      if (!isEnabled) return Promise.reject(new InvalidSystemError());

      return new Promise<boolean>((resolve, reject) => {
        Office.context.ui.displayDialogAsync(
          `${window.location.origin}/login.html?url=${encodeURIComponent(formattedUrl)}`,
          { height: 50, width: 35 },
          function (asyncResult) {
            if (asyncResult.error || asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
              asyncResult.value.close();
              reject(new AuthError('Failed to initiate the auth flow'));
              return;
            }

            asyncResult.value.addEventHandler(Office.EventType.DialogEventReceived, () => {
              asyncResult.value.close();
              reject(new AuthError('Authentication aborted prior to successful login'));
              return;
            });

            asyncResult.value.addEventHandler(Office.EventType.DialogMessageReceived, (msg) => {
              if ('error' in msg) {
                asyncResult.value.close();
                reject(new AuthError('Token callback unsuccessful'));
                return;
              }
              setToken(msg.message);
              asyncResult.value.close();
              resolve(true);
            });
          },
        );
      });
    },
    [setToken, setEnterpriseUrl],
  );

  React.useEffect(() => {
    const initializeClient = async () => {
      if (!token || !clientRef.current) return;

      try {
        await clientRef.current.setAuthToken(token);
        navigate('main');
        const configuration = await clientRef.current.fetchConfiguration();
        setConfiguration(configuration);
      } catch (error) {
        handleAuthError(error as Error);
      }
    };

    initializeClient();
  }, [token, navigate, handleAuthError]);

  const refetchConfiguration = React.useCallback(async () => {
    const configuration = await clientRef.current.fetchConfiguration();
    setConfiguration(configuration);
    return configuration;
  }, []);

  const pushEmail = React.useCallback((data: CreateEmailObjectRequest) => {
    return clientRef.current.createEmail(data);
  }, []);

  const useFetchStakeholders = React.useCallback(
    (emails: Office.EmailAddressDetails[]) => {
      const [isLoading, setIsLoading] = React.useState(false);
      const [matchingRecords, setMatchingStakeholders] = React.useState<StakeholderInformation[]>([]);

      const fetchEmails = React.useCallback(async () => {
        setIsLoading(true);
        try {
          if (!clientRef.current) return;
          const stakeholderMap = await clientRef.current.findExistingStakeholders(emails.map((x) => x.emailAddress));
          setMatchingStakeholders(stakeholderMap);
        } catch (e) {
          console.error(e);
          if (e instanceof AuthError) {
            logout();
          }
        } finally {
          setIsLoading(false);
        }
      }, [emails]);

      React.useEffect(() => {
        fetchEmails();
      }, [fetchEmails]);

      return { isLoading, matchingRecords, refetch: fetchEmails };
    },
    [logout],
  );

  const useFetchUsers = React.useCallback(
    (emails: Office.EmailAddressDetails[]) => {
      const [isLoading, setIsLoading] = React.useState(false);
      const [matchingRecords, setMatchingUsers] = React.useState<StakeholderInformation[]>([]);

      const fetchEmails = React.useCallback(async () => {
        setIsLoading(true);
        try {
          if (!clientRef.current) return;
          const users = await clientRef.current.findExistingUsers(emails.map((x) => x.emailAddress));
          setMatchingUsers(users);
        } catch (e) {
          console.error(e);
          if (e instanceof AuthError) {
            logout();
          }
        } finally {
          setIsLoading(false);
        }
      }, [emails]);

      React.useEffect(() => {
        fetchEmails();
      }, [fetchEmails]);

      return { isLoading, matchingRecords, refetch: fetchEmails };
    },
    [logout],
  );

  const createStakeholder = React.useCallback(
    async (stakeholderData: StakeholderCreationRequest) => {
      try {
        if (!clientRef.current) return null;
        return await clientRef.current.createStakeholder(stakeholderData);
      } catch (e) {
        if (e instanceof AuthError) {
          logout();
        } else {
          throw e;
        }
      }
      return null;
    },
    [logout],
  );

  const checkSummary = React.useCallback(
    async (emailData: RawEmailData) => {
      try {
        if (!clientRef.current) return null;
        return await clientRef.current.getSummary(emailData);
      } catch (e) {
        if (e.statusCode === 404) return null;
        console.error(e);
        if (e instanceof AuthError) {
          logout();
        }
      }
      return null;
    },
    [logout],
  );

  const contextValue = React.useMemo(
    () => ({
      configuration,
      initiateAuth,
      enterpriseUrl,
      isAuthenticated: clientRef.current?.isAuthenticated ?? false,
      logout,
      pushEmail,
      useFetchStakeholders,
      useFetchUsers,
      createStakeholder,
      checkSummary,
      refetchConfiguration,
    }),
    [
      configuration,
      initiateAuth,
      enterpriseUrl,
      logout,
      pushEmail,
      useFetchStakeholders,
      useFetchUsers,
      createStakeholder,
      checkSummary,
      refetchConfiguration,
    ],
  );

  return <ClientContext.Provider value={contextValue}>{getChildren(children, contextValue)}</ClientContext.Provider>;
};

export const useClient = () => {
  const context = React.useContext(ClientContext);
  if (!context) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};

export default ClientProvider;
